<template>
	<div class="cardholder-picker">
		<AppList :items="cardholdersItems">
			<template #new-cardholder>
				<div class="new-cardholder">
					<AppList :items="newCardholderItems" >
						<template #input-address="{item}">
							<div class="row centered space-between list-row">
								<p class="list-item-title">{{ item.title }}</p>
								<AppGoogleMapsInput class="text-input" no-map :placeholder="item.title" @changed-address="(a) => newCardholderAddress = a" />
							</div>
						</template>
						<template v-slot:input-calendar="{item}">
							<div class="row centered space-between list-row">
								<p class="list-item-title">{{ item.title }}</p>
								<DatePicker ref="date-picker" v-model="newCardholderBirthDate" mode="date" color="teal" :max-date='sixteenYearsAgo' :is-dark="userStore.darkMode">
									<template #default="{ togglePopover }">
										<AppTextInput class="text-input" @click="togglePopover()" :value="birthDateReadable" :placeholder="getString('birth_date')" readonly />
									</template>
								</DatePicker>
							</div>
						</template>
					</AppList>
				</div>
			</template>
		</AppList>
	</div>
</template>

<script>
import {defineComponent} from 'vue'
import {validateEmail, validatePhone} from "@/shared/helpers/strings";
import AppList from "@/shared/components/AppList.vue";
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import {useUserStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {formatDateMMDDHHmm, formatDateYYMMDD} from "@/shared/helpers/dates";

export default defineComponent({
	setup() {
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {cardsStore, getString: languageStore.getString, notificationStore, userStore}
	},
	name: "CardholderPicker",
	components: {AppTextInput, AppGoogleMapsInput, AppList},
	emits: ["selectCardholder"],
	data() {
		return {
			newCardholderOpen: false,
			newCardholderName: "",
			newCardholderSurname: "",
			newCardholderEmail: "",
			newCardholderPhone: "",
			newCardholderBirthDate: null,
			newCardholderAddress: null,
			loadingCreate: false,
		}
	},
	computed: {
		birthDateReadable() {
			if(!this.newCardholderBirthDate) return "";
			const date = new Date(this.newCardholderBirthDate);
			return formatDateYYMMDD(date);
		},
		cardholdersItems() {
			let existing = this.cardsStore.businessCardholders ? this.cardsStore.businessCardholders.map(cardholder => ({
				title: cardholder.name,
				buttonTitle: this.getString("select"),
				callbackButton: () => {
					this.$emit("selectCardholder", {
						cardholder_id: cardholder.cardholder_id,
						name: cardholder.name,
					});
				},
			})) : [];
			existing.push({
				title: this.getString("new_cardholder"),
				chevronItem: true,
				active: this.newCardholderOpen,
				dropdownSlot: 'new-cardholder',
			})
			return existing;
		},
		sixteenYearsAgo() {
			const date = new Date();
			date.setFullYear(date.getFullYear() - 16);
			return date;
		},
		newCardholderItems() {
			return [{
				title: this.getString("cardholder_name"),
				textInput: true,
				placeholder: this.getString('cardholder_name'),
				value: this.newCardholderName,
				callbackText: (value) => {
					this.newCardholderName = value;
				},
			},{
				title: this.getString("cardholder_surname"),
				textInput: true,
				placeholder: this.getString('cardholder_surname'),
				value: this.newCardholderSurname,
				callbackText: (value) => {
					this.newCardholderSurname = value;
				},
			}, {
                title: this.getString("address"),
                customSlot: 'input-address',
            }, {
				title: this.getString("email"),
				textInput: true,
				placeholder: this.getString('email'),
				inputType: "email",
				value: this.newCardholderEmail,
				callbackText: (value) => {
					this.newCardholderEmail = value;
				},
			}, {
				title: this.getString("phone"),
				textInput: true,
				explain: this.getString('phone_used_for_3ds'),
				placeholder: this.getString('phone'),
				inputType: "phone",
				value: this.newCardholderPhone,
				callbackText: (value) => {
					this.newCardholderPhone = value;
				},
			}, {
				title: this.getString("birth_date"),
				customSlot: 'input-calendar',
			}, {
				title: this.getString("create_cardholder"),
				buttonTitle: this.getString("create"),
				buttonLoading: this.loadingCreate,
				callbackButton: () => {
					this.createCardholder();
				},
			}
			];
		},
	},
	mounted() {
		this.newCardholderOpen = !(this.cardsStore.businessCardholders && this.cardsStore.businessCardholders.length > 0);
	},
	methods: {
		createCardholder() {
			if(this.newCardholderName.length < 2) {
				this.notificationStore.showNotification("invalid-name");
				return false;
			}
			if(this.newCardholderSurname.length < 2) {
				this.notificationStore.showNotification("invalid-name");
				return false;
			}
			if(this.newCardholderName.length+1+this.newCardholderSurname.length >= 24) {
				this.notificationStore.showNotification("invalid-name");
				return false;
			}
			if(!validateEmail(this.newCardholderEmail)) {
				this.notificationStore.showNotification("invalid-email");
				return false;
			}
			if(!validatePhone(this.newCardholderPhone)) {
				this.notificationStore.showNotification("invalid-phone");
				return false;
			}
			if(!this.newCardholderAddress) {
				this.notificationStore.showNotification("error-address");
				return false;
			}
			// Min 16 years old
			if(!this.newCardholderBirthDate || (new Date(this.newCardholderBirthDate).getTime() > (new Date().getTime() - 16 * 365 * 24 * 60 * 60 * 1000))) {
				this.notificationStore.showNotification("wrong-date");
				return false;
			}
			const date = formatDateYYMMDD(new Date(this.newCardholderBirthDate));

			this.loadingCreate = true;
			API.init().newCardholder(this.userStore.currBusinessId, this.newCardholderName, this.newCardholderSurname, this.newCardholderEmail, this.newCardholderPhone, JSON.stringify(this.newCardholderAddress), date, (res) => {
				this.loadingCreate = false;
				if(res && res.result === "OK") {
					this.cardsStore.getBusinessCards()

					this.$emit("selectCardholder", {
						cardholder_id: res.cardholder_id,
						name: this.newCardholderName + " " + this.newCardholderSurname,
					});

					this.newCardholderName = "";
					this.newCardholderSurname = "";
					this.newCardholderEmail = "";
					this.newCardholderPhone = "";
					this.newCardholderAddress = null;
					this.newCardholderOpen = false;
				} else if(res.details && res.details === "invalid_address") {
					this.notificationStore.showNotification("error-address");
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		}
	}
})
</script>

<style scoped lang="scss">
.list-row {
    gap: 10px;
    .map-input-container {
        margin-left: auto;
        &:deep(.address-input) {
            width: 240px;

            @media (max-width: 1250px) {
                width: 190px;
            }
        }
        &:deep(.second-row) {
            justify-content: flex-end;
            width: 100%;
            max-width: 280px;
        }
        &:deep(.input-wrapper .input-optional) {
            width: 100px;
        }
    }
	.text-input {
		width: 240px;
		@media (max-width: 1250px) {
			width: 190px;
		}
	}
}
</style>